import { useRef } from 'react';
import styled from 'styled-components';
import { withTranslation } from 'react-i18next';

import ProfileImage from '@repo/ui/components/ui/ProfileImage';
import Icon from '@repo/ui/components/ui/Icon';
import { CollectionItemDetail, CollectionItemHeader } from '@repo/ui/components/typography/Content';
import Link from '@repo/ui/components/buttons/Link';
import DropdownView from '@repo/ui/components/buttons/DropdownView';

const Container = styled.button`
  display: flex;
  align-items: center;
  flex-direction: row;

  background-color: ${(props) => props.theme.brandColors.lightestBackground};
  ${(props) => props.theme.borders.inner};
  margin-left: 11px;
  padding: 2px;
  cursor: pointer;
  &:hover {
    border-color: ${(props) => props.theme.brandColors.hsla(props.theme.brandColors.tertiary)};
  }
  & > :first-child {
    margin: 0 2px;
  }
  & i {
    padding: 0 7px;
  }

  ${(props) => props.theme.media.phone`margin-right: 3px`};
`;

const Title = styled.div`
  padding: ${(props) => props.theme.margins.xs};
`;

const MenuContainer = styled.div`
  & > ul > li > a {
    font-weight: 400;
    color: ${(props) => props.theme.brandColors.primaryText} !important;
  }
`;

function UserDropDown({ id, isProxy, user, t }) {
  const dropdownRef = useRef(null);
  const menuToggleRef = useRef(null);
  const firstMenuLinkRef = useRef(null);

  const handleClose = (byKeyboard: boolean = false) => {
    if (byKeyboard) menuToggleRef?.current?.focus();
    dropdownRef?.current?.close();
  };

  const handleOpen = (byKeyboard: boolean = false) => {
    // Focus the first menu link when dropdown opens by keyboard
    if (byKeyboard) {
      setTimeout(() => {
        firstMenuLinkRef?.current?.focus();
      }, 0);
    }
  };

  const dropdownMenu = (
    <MenuContainer role="menu" id="user-menu" aria-labelledby={id}>
      <Title>
        <CollectionItemHeader aria-label={`Logged in as ${user.firstName} ${user.lastName}`}>
          {isProxy ? 'Acting as: ' : ''}
          {user.firstName} {user.lastName}
        </CollectionItemHeader>
      </Title>
      <ul className="fa-ul dropdown-menu-items" aria-label="User actions">
        <li>
          <Link
            ref={firstMenuLinkRef}
            to={`/user/profile/${user.hash}/volunteerism`}
            onClick={handleClose}
            role="menuitem"
          >
            <Icon name="user" /> {t('common:nav.myProfile')}
          </Link>
        </li>
        <li>
          <Link to="/user/settings" onClick={handleClose} role="menuitem">
            <Icon name="cog" /> {t('common:nav.settingsAndPrivacy')}
          </Link>
        </li>
        <li>
          <Link
            href="https://support.dosomegood.ca/hc/en-us/"
            target="_blank"
            rel="noopener noreferrer"
            role="menuitem"
          >
            <Icon name="questionmark" /> {t('common:nav.helpCenter')}
          </Link>
        </li>
        <li>
          <Link
            href="https://support.dosomegood.ca/hc/en-us/requests/new"
            target="_blank"
            rel="noopener noreferrer"
            role="menuitem"
          >
            <Icon name="commenting" /> {t('common:nav.submitFeedback')}
          </Link>
        </li>
        <li>
          <Link to="/logout" onClick={handleClose}>
            <Icon name="enter" /> {t('commonPlat:actions.logout')}
          </Link>
        </li>
      </ul>

      {(user.userType >= 1 || !!isProxy) && (
        <>
          <Title>
            <CollectionItemHeader>STAFF OPTIONS</CollectionItemHeader>
          </Title>

          {isProxy ? (
            <ul className="fa-ul dropdown-menu-items" aria-label="Staff Options">
              <li className="text-primary">
                <Link to="/logout-of-proxy" onClick={handleClose} role="menuitem">
                  <Icon name="enter" /> {t('common:nav.exitProxy')}
                </Link>
              </li>
            </ul>
          ) : (
            <ul className="fa-ul dropdown-menu-items" aria-label="Staff Options">
              <li>
                <Link to="/staff" onClick={handleClose} role="menuitem">
                  <Icon name="cog" /> {t('common:nav.dashboard')}
                </Link>
              </li>
              <li>
                <Link to="/dashboard/staff" onClick={handleClose} role="menuitem">
                  <Icon name="news" /> News Promotion
                </Link>
              </li>
              <li>
                <Link to="/staff/users" onClick={handleClose} role="menuitem">
                  <Icon name="group" /> {t('common:nav.userManagement')}
                </Link>
              </li>
            </ul>
          )}
        </>
      )}
    </MenuContainer>
  );

  return (
    <DropdownView
      dropdownRef={dropdownRef}
      aria-label={`Do Some Good account: ${user.firstName} ${user.lastName}`}
      aria-controls="user-menu"
      onOpen={handleOpen}
      buttonComponent={
        <Container
          id={id}
          tabIndex={0}
          role="button"
          aria-label={`Do Some Good account: ${user.firstName} ${user.lastName}`}
          ref={menuToggleRef}
        >
          <ProfileImage
            type="user"
            size="xs"
            name={`${user.firstName} ${user.lastName}`}
            image={user.profilePhoto}
          />
          <CollectionItemDetail style={{ padding: '2px 0 0 4px' }}>
            <span>{user.firstName}</span>
            <span className="hidden-xs"> {user.lastName}</span>
          </CollectionItemDetail>
          <Icon name="ellipses-v" />
        </Container>
      }
      bottom
      attachRight
      fill
      fixed
    >
      {dropdownMenu}
    </DropdownView>
  );
}

export default withTranslation(['common', 'commonPlat'])(UserDropDown);
